import type {Option} from "@/types/data-table";
import type {Column} from "@tanstack/react-table";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import {Popover, PopoverContent, PopoverTrigger,} from "@/components/ui/popover";
import {Separator} from "@/components/ui/separator";
import * as React from "react";
import {AddCircleOutline} from "@mui/icons-material";
import {Checkbox} from "components/ui/checkbox";

interface DataTableFacetedFilterProps<TData, TValue> {
    column?: Column<TData, TValue>;
    title?: string;
    options: Option[];
    multiple?: boolean;
    itemHeight?: number;
}

export function DataTableFacetedFilter<TData, TValue>({
                                                          column,
                                                          title,
                                                          options,
                                                          multiple, itemHeight = 8
                                                      }: DataTableFacetedFilterProps<TData, TValue>) {
    const [open, setOpen] = React.useState(false);

    const columnFilterValue = column?.getFilterValue();
    const selectedValues = new Set(
        Array.isArray(columnFilterValue) ? columnFilterValue : [],
    );

    const onItemSelect = React.useCallback(
        (option: Option, isSelected: boolean) => {
            if (!column) return;

            if (multiple) {
                const newSelectedValues = new Set(selectedValues);
                if (isSelected) {
                    newSelectedValues.delete(option.value);
                } else {
                    newSelectedValues.add(option.value);
                }
                const filterValues = Array.from(newSelectedValues);
                column.setFilterValue(filterValues.length ? filterValues : undefined);
            } else {
                column.setFilterValue(isSelected ? undefined : [option.value]);
                setOpen(false);
            }
        },
        [column, multiple, selectedValues],
    );

    const onReset = React.useCallback(
        (event?: React.MouseEvent) => {
            event?.stopPropagation();
            column?.setFilterValue(undefined);
        },
        [column],
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm"
                        className={`text-gray3  border h-${itemHeight} ${itemHeight !== 8 ? "text-md font-normal" : ""}`}>
                    {selectedValues?.size > 0 ? (
                        <div
                            role="button"
                            aria-label={`Clear ${title} filter`}
                            tabIndex={0}
                            onClick={onReset}
                            className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                            <AddCircleOutline sx={{width: 18, height: 18}}/>
                        </div>
                    ) : (
                        <AddCircleOutline sx={{width: 18, height: 18}}/>
                    )}
                    {title}
                    {selectedValues?.size > 0 && (
                        <>
                            <Separator
                                orientation="vertical"
                                className="mx-0.5 data-[orientation=vertical]:h-4"
                            />
                            <Badge
                                variant="secondary"
                                className="rounded-sm px-1 font-normal lg:hidden"
                            >
                                {selectedValues.size}
                            </Badge>
                            <div className="hidden items-center gap-1 lg:flex">
                                {selectedValues.size > 2 ? (
                                    <Badge
                                        variant="secondary"
                                        className="rounded-sm px-1 font-normal"
                                    >
                                        {selectedValues.size} sélections
                                    </Badge>
                                ) : (
                                    options
                                        .filter((option) => selectedValues.has(option.value))
                                        .map((option) => (
                                            <Badge
                                                variant="secondary"
                                                key={option.value}
                                                className="rounded-sm px-1 font-normal"
                                            >
                                                {option.label}
                                            </Badge>
                                        ))
                                )}
                            </div>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[12.5rem] p-0" align="start">
                <Command>
                    <CommandInput placeholder={title}/>
                    <CommandList className="max-h-full">
                        <CommandEmpty>Aucune donnée trouvée.</CommandEmpty>
                        <CommandGroup className="max-h-[18.75rem] overflow-y-auto overflow-x-hidden">
                            {options.map((option) => {
                                const isSelected = selectedValues.has(option.value);

                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => onItemSelect(option, isSelected)}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            aria-label="Select option"
                                        />
                                        {option.icon && <option.icon/>}
                                        <span className="truncate">{option.label}</span>
                                        {option.count && (
                                            <span className="ml-auto font-mono text-xs">
                        {option.count}
                      </span>
                                        )}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {selectedValues.size > 0 && (
                            <>
                                <CommandSeparator/>
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={() => onReset()}
                                        className="justify-center text-center"
                                    >
                                        Retirer le filtre
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
